import {
  AddressLookupTableAccount,
  Connection,
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import * as multisig from '@sqds/multisig';
import bs58 from 'bs58';
import { useWalletStore } from '@/stores/wallet';
import { useMultisigStore } from '@/stores/multisig';
import { signedFetch } from '@/lib/signedFetch';

export interface TxFlowAuth {
  mode: 'totp' | 'session' | 'recovery';
  code: string;
}

export interface TxFlowInput {
  /** Inner Solana instructions from the dApp (already decoded). */
  innerMessage: TransactionMessage;
  /** Optional ALTs the inner message references. */
  addressLookupTables?: AddressLookupTableAccount[];
  /** RPC URL. */
  rpcUrl: string;
  /** Squads program id (from /config). */
  programId: string;
  /** Auth used for the backend `/sign` call. */
  auth: TxFlowAuth;
  /** Optional progress hook for the UI. */
  onProgress?: (status: TxFlowStatus) => void;
}

export type TxFlowStatus =
  | { kind: 'reading_index' }
  | { kind: 'awaiting_user_signature' }
  | { kind: 'submitting_create' }
  | { kind: 'awaiting_backend_signature'; transactionIndex: string }
  | { kind: 'submitting_execute' }
  | { kind: 'done'; createSig: string; backendSig: string; executeSig: string };

export interface TxFlowResult {
  transactionIndex: string;
  multisigPda: string;
  createSig: string;
  backendSig: string;
  executeSig: string;
}

/**
 * Run the full Squads vault transaction flow for a dApp request:
 *   1. user signs `[vaultTransactionCreate, proposalCreate, proposalApprove(user)]`
 *   2. backend signs `proposalApprove(backend)` (gated by 2FA)
 *   3. user signs `vaultTransactionExecute`
 *
 * Returns the three signatures plus the new transactionIndex.
 */
export async function runTransactionFlow(input: TxFlowInput): Promise<TxFlowResult> {
  const wallet = useWalletStore.getState();
  const ms = useMultisigStore.getState();
  const userPk = wallet.publicKey();
  if (!userPk) throw new Error('connect wallet first');
  if (!ms.activePda) throw new Error('no active multisig');
  const multisigPdaPk = new PublicKey(ms.activePda);
  const programId = new PublicKey(input.programId);
  const conn = new Connection(input.rpcUrl, 'confirmed');

  const progress = input.onProgress ?? (() => {});

  progress({ kind: 'reading_index' });
  const onChain = await multisig.accounts.Multisig.fromAccountAddress(conn, multisigPdaPk);
  const newIndex = BigInt(onChain.transactionIndex.toString()) + 1n;

  const createIx = multisig.instructions.vaultTransactionCreate({
    multisigPda: multisigPdaPk,
    transactionIndex: newIndex,
    creator: userPk,
    vaultIndex: 0,
    ephemeralSigners: 0,
    transactionMessage: input.innerMessage,
    addressLookupTableAccounts: input.addressLookupTables,
    programId,
  });
  const proposalIx = multisig.instructions.proposalCreate({
    multisigPda: multisigPdaPk,
    transactionIndex: newIndex,
    creator: userPk,
    programId,
  });
  const userVoteIx = multisig.instructions.proposalApprove({
    multisigPda: multisigPdaPk,
    transactionIndex: newIndex,
    member: userPk,
    programId,
  });

  progress({ kind: 'awaiting_user_signature' });
  const blockhash1 = await conn.getLatestBlockhash('confirmed');
  const message1 = new TransactionMessage({
    payerKey: userPk,
    recentBlockhash: blockhash1.blockhash,
    instructions: [createIx, proposalIx, userVoteIx],
  }).compileToV0Message(input.addressLookupTables);
  const tx1 = new VersionedTransaction(message1);
  const signedTx1 = await wallet.signTransaction(tx1);

  progress({ kind: 'submitting_create' });
  const createSig = await conn.sendRawTransaction(signedTx1.serialize());
  await conn.confirmTransaction(
    { signature: createSig, blockhash: blockhash1.blockhash, lastValidBlockHeight: blockhash1.lastValidBlockHeight },
    'confirmed',
  );

  progress({ kind: 'awaiting_backend_signature', transactionIndex: newIndex.toString() });
  const fields: Record<string, string | number> = { 'Transaction Index': newIndex.toString() };
  if (input.auth.mode === 'totp') fields['Totp Code'] = input.auth.code;
  else if (input.auth.mode === 'session') fields['Session Token'] = input.auth.code;
  else fields['Recovery Code'] = input.auth.code;

  const signRes = await signedFetch<{ signature: string | null; alreadyApproved?: boolean }>(
    'transaction.sign',
    { fields, ttlSec: 120 },
  );
  if (!signRes.ok) {
    const message = (signRes.data as { message?: string })?.message;
    throw new Error(`backend sign failed (${signRes.status}): ${message ?? 'unknown'}`);
  }
  const backendSig =
    signRes.data.signature ??
    (signRes.data.alreadyApproved ? 'already-approved' : '');

  const exec = await multisig.instructions.vaultTransactionExecute({
    connection: conn,
    multisigPda: multisigPdaPk,
    transactionIndex: newIndex,
    member: userPk,
    programId,
  });

  progress({ kind: 'submitting_execute' });
  const blockhash2 = await conn.getLatestBlockhash('confirmed');
  const message2 = new TransactionMessage({
    payerKey: userPk,
    recentBlockhash: blockhash2.blockhash,
    instructions: [exec.instruction],
  }).compileToV0Message(exec.lookupTableAccounts);
  const tx2 = new VersionedTransaction(message2);
  const signedTx2 = await wallet.signTransaction(tx2);
  const executeSig = await conn.sendRawTransaction(signedTx2.serialize());
  await conn.confirmTransaction(
    { signature: executeSig, blockhash: blockhash2.blockhash, lastValidBlockHeight: blockhash2.lastValidBlockHeight },
    'confirmed',
  );

  progress({ kind: 'done', createSig, backendSig, executeSig });

  return {
    transactionIndex: newIndex.toString(),
    multisigPda: ms.activePda,
    createSig,
    backendSig,
    executeSig,
  };
}

/** Decode a base64 / base58 transaction string into the instructions + ALTs needed by `runTransactionFlow`. */
export async function decodeDappTransaction(
  serialized: string,
  rpcUrl: string,
): Promise<{ message: TransactionMessage; addressLookupTables: AddressLookupTableAccount[] }> {
  let bytes: Uint8Array;
  try {
    bytes = bs58.decode(serialized);
  } catch {
    bytes = Uint8Array.from(atob(serialized), (c) => c.charCodeAt(0));
  }
  const versioned = VersionedTransaction.deserialize(bytes);
  const conn = new Connection(rpcUrl, 'confirmed');
  const altKeys = versioned.message.addressTableLookups.map((l) => l.accountKey);
  const altAccounts: AddressLookupTableAccount[] = [];
  for (const key of altKeys) {
    const info = await conn.getAddressLookupTable(key);
    if (info.value) altAccounts.push(info.value);
  }
  const decompiled = TransactionMessage.decompile(versioned.message, {
    addressLookupTableAccounts: altAccounts,
  });
  return { message: decompiled, addressLookupTables: altAccounts };
}
